import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { Card, CardContent, Loader } from '@common/components';
import {
  selectSyncMonitor,
  setSelectedCenters,
  setYear,
  setMonth,
  setView,
  openDayDrawer,
  closeDayDrawer,
  setStatusFilter,
  clearStatusFilter,
  setGlobalSelectedDate,
  clearGlobalSelectedDate,
  setTags,
  DEFAULT_TAGS,
} from '@dashboard/slices/syncMonitor';
import {
  useSyncCalendarQuery,
  useSyncStatsSummaryQuery,
  useSyncGlobalCalendarQuery,
  useSyncGlobalOverviewQuery,
  useSyncPeriodTotalsQuery,
} from '@dashboard/api/syncMonitor';
import CenterSearchSelect from '@dashboard/components/SyncMonitor/CenterSearchSelect';
import CalendarControls from '@dashboard/components/SyncMonitor/CalendarControls';
import SyncCalendar from '@dashboard/components/SyncMonitor/SyncCalendar';
import GlobalCalendar from '@dashboard/components/SyncMonitor/GlobalCalendar';
import SyncDayDrawer from '@dashboard/components/SyncMonitor/SyncDayDrawer';
import GlobalDayDetail from '@dashboard/components/SyncMonitor/GlobalDayDetail';
import StatsSummaryPanel from '@dashboard/components/SyncMonitor/StatsSummaryPanel';
import RecentByStatusList from '@dashboard/components/SyncMonitor/RecentByStatusList';
import HolesPanel from '@dashboard/components/SyncMonitor/HolesPanel';
import AnomaliesPanel from '@dashboard/components/SyncMonitor/AnomaliesPanel';
import TagFilter from '@dashboard/components/SyncMonitor/TagFilter';
import classes from './SyncMonitorView.module.scss';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i).reverse();

export default function SyncMonitorView() {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState('calendar');
  const {
    selectedCenters,
    year,
    month,
    view,
    drawerOpen,
    drawerCenter,
    drawerDate,
    statusFilter,
    globalSelectedDate,
    tags: tagsByTab,
  } = useAppSelector(selectSyncMonitor);
  const tags = tagsByTab[tab];

  const centerIds = selectedCenters.map((c) => c.value);
  const isGlobalMode = centerIds.length === 0;

  // ─── Center mode (calendar by status) ───
  const calendarFrom = view === 'year' ? `${year}-01-01` : `${year}-${String(month).padStart(2, '0')}-01`;
  const calendarTo = view === 'year'
    ? `${year}-12-31`
    : `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;

  const { data: calendarResp, isFetching: isCalendarFetching } = useSyncCalendarQuery(
    { centerIds, from: calendarFrom, to: calendarTo },
    { skip: isGlobalMode || tab !== 'calendar' },
  );
  const calendarData = calendarResp?.data ?? {};

  // ─── Global mode (aggregated counts per day) ───
  const { data: globalResp, isFetching: isGlobalFetching } = useSyncGlobalCalendarQuery(
    { year, month: view === 'month' ? month : null, tags },
    { skip: !isGlobalMode || tab !== 'calendar' },
  );
  const globalData = globalResp?.data ?? {};

  // ─── Header summary tiles (works for both modes) ───
  const { data: summaryResp, isFetching: isSummaryFetching } = useSyncStatsSummaryQuery({
    centerIds,
    year,
    month: view === 'month' ? month : null,
    tags,
  }, { skip: tab !== 'calendar' });
  const summaryData = summaryResp?.data ?? null;

  // ─── Global overview (always — independent of filters) ───
  const { data: overviewResp } = useSyncGlobalOverviewQuery({ tags });
  const overviewData = overviewResp?.data ?? null;

  // ─── Period totals for the calendar selectors (respects center filter) ───
  const { data: periodResp } = useSyncPeriodTotalsQuery({ centerIds, year, tags });
  const periodData = periodResp?.data ?? null;

  const handleCenterDayClick = useCallback(
    (centerId, date) => dispatch(openDayDrawer({ centerId, date })),
    [dispatch],
  );
  const handleGlobalDayClick = useCallback(
    (date) => {
      if (date === globalSelectedDate) {
        dispatch(clearGlobalSelectedDate());
      } else {
        dispatch(setGlobalSelectedDate(date));
      }
    },
    [dispatch, globalSelectedDate],
  );

  // ─── Calendar block ───
  const calendarBlock = () => {
    if (isGlobalMode) {
      return isGlobalFetching ? (
        <div className={classes.loaderWrap}><Loader /></div>
      ) : (
        <GlobalCalendar
          globalData={globalData}
          view={view}
          year={year}
          month={month}
          selectedDate={globalSelectedDate}
          onDayClick={handleGlobalDayClick}
        />
      );
    }
    return isCalendarFetching ? (
      <div className={classes.loaderWrap}><Loader /></div>
    ) : (
      <SyncCalendar
        calendarData={calendarData}
        view={view}
        year={year}
        month={month}
        centerIds={centerIds}
        onDayClick={handleCenterDayClick}
      />
    );
  };

  // ─── Detail block ───
  const detailBlock = () => {
    if (isGlobalMode) {
      return (
        <GlobalDayDetail
          date={globalSelectedDate}
          counts={globalSelectedDate ? globalData[globalSelectedDate] : null}
          onClose={() => dispatch(clearGlobalSelectedDate())}
        />
      );
    }

    if (drawerOpen) {
      return (
        <SyncDayDrawer
          isOpen={drawerOpen}
          onClose={() => dispatch(closeDayDrawer())}
          centerId={drawerCenter}
          date={drawerDate}
        />
      );
    }
    return (
      <div className={classes.detailPlaceholder}>
        <p>{t('detailPlaceholder')}</p>
      </div>
    );
  };

  return (
    <section className={classes.view}>
      {/* ── Area 1 — Header (search · view · stats / selectors / summary) ── */}
      <Card className={classes.headerCard}>
        <CardContent>
          <div className={classes.toolbar}>
            <CenterSearchSelect
              value={selectedCenters}
              onChange={(v) => dispatch(setSelectedCenters(v || []))}
            />

            <div className={classes.viewToggleInline}>
              <button
                type="button"
                className={`${classes.viewBtn} ${tab === 'calendar' ? classes.viewBtnActive : ''}`}
                onClick={() => setTab('calendar')}
              >
                {t('tabCalendar')}
              </button>
              <button
                type="button"
                className={`${classes.viewBtn} ${tab === 'holes' ? classes.viewBtnActive : ''}`}
                onClick={() => setTab('holes')}
              >
                {t('tabHoles')}
              </button>
              <button
                type="button"
                className={`${classes.viewBtn} ${tab === 'anomalies' ? classes.viewBtnActive : ''}`}
                onClick={() => setTab('anomalies')}
              >
                {t('tabAnomalies')}
              </button>
            </div>

            {tab !== 'anomalies' && (
              <div className={classes.viewToggleInline}>
                <button
                  type="button"
                  className={`${classes.viewBtn} ${view === 'year' ? classes.viewBtnActive : ''}`}
                  onClick={() => dispatch(setView('year'))}
                >
                  {t('viewYear')}
                </button>
                <button
                  type="button"
                  className={`${classes.viewBtn} ${view === 'month' ? classes.viewBtnActive : ''}`}
                  onClick={() => dispatch(setView('month'))}
                >
                  {t('viewMonth')}
                </button>
              </div>
            )}

            {overviewData && (
              <div className={classes.overview} title={t('overviewTooltip')}>
                <span className={classes.overviewItem}>
                  <strong>{overviewData.total_centers ?? 0}</strong>
                  <span className={classes.overviewLabel}>{t('overviewCenters')}</span>
                </span>
                <span className={classes.overviewItem}>
                  <strong>{overviewData.total_records ?? 0}</strong>
                  <span className={classes.overviewLabel}>{t('overviewRecords')}</span>
                </span>
                {overviewData.first_date && (
                  <span className={classes.overviewItem}>
                    <strong>{overviewData.first_date}</strong>
                    <span className={classes.overviewLabel}>{t('overviewFirstDate')}</span>
                  </span>
                )}
                {overviewData.last_date && (
                  <span className={classes.overviewItem}>
                    <strong>{overviewData.last_date}</strong>
                    <span className={classes.overviewLabel}>{t('overviewLastDate')}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className={classes.tagsWrap}>
            <TagFilter
              value={tags}
              defaults={DEFAULT_TAGS[tab]}
              onChange={(next) => dispatch(setTags({ tab, tags: next }))}
            />
          </div>

          {tab !== 'anomalies' && (
          <div className={classes.controlsWrap}>
            <CalendarControls
              year={year}
              month={month}
              view={view}
              yearOptions={YEAR_OPTIONS}
              yearTotals={periodData?.by_year}
              monthTotals={periodData?.by_month}
              onYearChange={(y) => dispatch(setYear(y))}
              onMonthChange={(m) => dispatch(setMonth(m))}
            />
          </div>
          )}

          {tab === 'calendar' && (
            <div className={classes.legendWrap}>
              <StatsSummaryPanel
                summary={summaryData}
                isFetching={isSummaryFetching}
                activeStatus={statusFilter}
                onSelectStatus={(s) =>
                  dispatch(s ? setStatusFilter(s) : clearStatusFilter())
                }
                year={year}
                month={month}
                view={view}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {tab === 'anomalies' && (
        <Card className={classes.listCard}>
          <CardContent className={classes.listCardContent}>
            <AnomaliesPanel tags={tags} />
          </CardContent>
        </Card>
      )}

      {tab === 'holes' && (
        <Card className={classes.listCard}>
          <CardContent className={classes.listCardContent}>
            <HolesPanel
              year={year}
              month={view === 'month' ? month : null}
              centerIds={centerIds}
              tags={tags}
            />
          </CardContent>
        </Card>
      )}

      {/* ── Area 2 — Calendar (40%) | Detail (60%) ──────────── */}
      {tab === 'calendar' && (
      <div className={classes.bodyGrid}>
        <Card className={classes.calendarCard}>
          <CardContent>{calendarBlock()}</CardContent>
        </Card>

        <Card className={classes.detailCard}>
          <CardContent>{detailBlock()}</CardContent>
        </Card>
      </div>
      )}

      {/* ── Area 3 — Recent registers for selected status ──── */}
      {tab === 'calendar' && statusFilter && (
        <Card className={classes.listCard}>
          <CardContent className={classes.listCardContent}>
            <RecentByStatusList
              status={statusFilter}
              centerIds={centerIds}
              year={year}
              month={view === 'month' ? month : null}
              date={isGlobalMode ? globalSelectedDate : null}
              limit={50}
              tags={tags}
              onClose={() => dispatch(clearStatusFilter())}
            />
          </CardContent>
        </Card>
      )}
    </section>
  );
}
